import { Link } from "react-router-dom";
import { Trophy, Lightning, Stairs, Sword, Fire, Star, Crown, Timer, Info } from "@phosphor-icons/react";

/**
 * /scoring — Public reference page explaining Season Score (SS), Knowledge
 * Rating (KR), per-mode formulas and Championship qualification.
 *
 * Auto-linked from every "What's new" Signal so players understand how their
 * numbers move.
 */
export default function Scoring() {
  return (
    <div className="max-w-4xl mx-auto p-4 sm:p-6 space-y-8" data-testid="scoring-page">
      <header>
        <p className="eyebrow mb-1">How you climb</p>
        <h1 className="font-display text-3xl sm:text-4xl font-medium text-white">Scoring</h1>
        <p className="text-sm text-white/60 mt-2 max-w-2xl">
          Every mode contributes to the same universal <strong className="text-nk-gold">Season Score (SS)</strong>.
          Duels also move a separate skill rating — <strong className="text-nk-gold">Knowledge Rating (KR)</strong>.
          Below is exactly how each number is earned.
        </p>
      </header>

      {/* Core metrics */}
      <section>
        <h2 className="eyebrow mb-3">Core metrics</h2>
        <div className="grid sm:grid-cols-2 gap-3">
          <MetricCard
            label="SS · Season Score"
            desc="Your cumulative points this 30-day season. Every mode contributes. Resets when the season ends."
            range="0 → unbounded"
            testid="metric-ss"
          />
          <MetricCard
            label="KR · Knowledge Rating"
            desc="ELO-style Duel-only skill rating. Starts at 1000. Beat higher-rated players → gain more; lose to lower-rated ones → lose more."
            range="100 (floor) → 3000+"
            testid="metric-kr"
          />
          <MetricCard
            label="Rating Peak"
            desc="Your highest KR ever. Lifetime record — never resets."
            testid="metric-peak"
          />
          <MetricCard
            label="Country Score"
            desc="Sum of every player's SS in your country. Drives the Country War rankings."
            testid="metric-country"
          />
        </div>
      </section>

      {/* Per-mode formulas */}
      <section>
        <h2 className="eyebrow mb-3">How each mode pays</h2>
        <div className="space-y-3">
          <ModeCard
            Icon={Lightning} name="NK Daily"
            cap="Up to 3,000 SS per show · 3 shows per day"
            formulaLines={[
              "75 SS per correct answer (base)",
              "+25 SS if you're fastest on that question",
              "+300 SS · perfect show (all correct)",
              "+400 SS · top-10 finish that show",
            ]}
            link="/daily"
          />
          <ModeCard
            Icon={Stairs} name="NK Ladder"
            cap="Up to 3,000 SS per climb"
            formulaLines={[
              "Floor 1 → 50 SS · Floor 5 → 200 · Floor 10 → 900 · Floor 15 → 3,000",
              "Safe rungs at floors 5 & 10 lock in your SS if you fall",
              "Higher floors pay disproportionately more — push past the safety net if you're confident",
            ]}
            link="/ladder"
          />
          <ModeCard
            Icon={Sword} name="NK Duel"
            cap="Winner up to 2,000 SS · Loser up to 1,000 SS"
            formulaLines={[
              "Base 500 (win) / 300 (loss)",
              "+800 SS win bonus",
              "+Accuracy up to 400 (win) / 500 (loss)",
              "+Speed up to 300 (win) / 200 (loss)",
              "Also adjusts your KR by ±16-32",
            ]}
            link="/duel"
          />
          <ModeCard
            Icon={Fire} name="NK Escape"
            cap="Up to 3,000 SS per run"
            formulaLines={[
              "Room 1 → 100 · Room 5 → 600 · Room 10 → 2,500 SS",
              "+500 SS · perfect run (all rooms without a wrong answer)",
              "One wrong answer ends the run",
            ]}
            link="/survival"
          />
          <ModeCard
            Icon={Star} name="Saturday Challenge" gold
            cap="20 SS base + 50 SS to the winner"
            formulaLines={[
              "Weekly show, higher prize pool",
              "Winner takes bonus + featured recap on Home",
            ]}
            link="/saturday"
          />
          <ModeCard
            Icon={Crown} name="Championship" gold
            cap="Prize money + Championship title"
            formulaLines={[
              "Held on Day 31 of every season",
              "Only qualified players enter (see below)",
              "Winner earns National / International title",
            ]}
            link="/championship"
          />
        </div>
      </section>

      {/* Bonuses */}
      <section>
        <h2 className="eyebrow mb-3">Bonuses that stack</h2>
        <div className="nk-card p-5 grid sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
          <BonusRow label="Perfect NK Daily show" value="+300 SS" />
          <BonusRow label="Top-10 in NK Daily show" value="+400 SS" />
          <BonusRow label="Perfect NK Escape run" value="+500 SS" />
          <BonusRow label="2-question combo" value="+250 SS" />
          <BonusRow label="3-question combo" value="+750 SS" />
          <BonusRow label="4-question combo" value="+1,500 SS" />
          <BonusRow label="Daily missions complete" value="+2,500 SS" />
          <BonusRow label="Weekly quests complete" value="+10,000 SS" />
          <BonusRow label="Daily Champion (top of the day)" value="+2,000 SS" />
        </div>
      </section>

      {/* Championship qualification */}
      <section>
        <h2 className="eyebrow mb-3">Championship qualification</h2>
        <div className="nk-card p-5 space-y-3">
          <p className="text-sm text-white/80">
            You qualify for the National Championship (Day 31) when <strong className="text-nk-gold">all three</strong> are true:
          </p>
          <ul className="space-y-2 text-sm text-white/80">
            <li className="flex gap-2">
              <span className="text-nk-gold">1.</span>
              <div>
                <strong>Top-10 in your country</strong> by Season Score.
              </div>
            </li>
            <li className="flex gap-2">
              <span className="text-nk-gold">2.</span>
              <div>
                <strong>Season Score ≥ 120,000</strong> (admin-adjustable per season).
              </div>
            </li>
            <li className="flex gap-2">
              <span className="text-nk-gold">3.</span>
              <div>
                <strong>Championship Checklist</strong> complete:
                <ul className="mt-1.5 space-y-0.5 text-xs text-white/60">
                  <li>• 20 NK Daily shows played</li>
                  <li>• 30 NK Duels won</li>
                  <li>• NK Escape room 8 reached</li>
                  <li>• NK Ladder floor 12 reached</li>
                  <li>• 80 daily missions completed</li>
                  <li>• 4 weekly quests completed</li>
                </ul>
              </div>
            </li>
          </ul>
        </div>
      </section>

      {/* KR deep-dive */}
      <section>
        <h2 className="eyebrow mb-3">KR · The duel-only ladder</h2>
        <div className="nk-card p-5 text-sm text-white/80 space-y-3">
          <p>
            Only NK Duel changes your KR (Knowledge Rating). Every other mode contributes to SS but leaves KR alone.
          </p>
          <p>
            <strong className="text-nk-gold">Starting rating: 1000.</strong> First 20 matches use a higher K-factor (32),
            so new players climb fast. Once you&apos;re established, the K-factor drops to 16 — your rating becomes stable
            and hard-won.
          </p>
          <p className="text-xs text-white/55">
            KR is the tiebreaker on the season leaderboard when two players share the same SS.
          </p>
        </div>
      </section>

      <section className="text-center pt-4">
        <Link
          to="/home"
          data-testid="scoring-back-home"
          className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-widest text-white/50 hover:text-nk-gold transition-colors"
        >
          ← Back to Home
        </Link>
      </section>
    </div>
  );
}

function MetricCard({ label, desc, range, testid }) {
  return (
    <div className="nk-card p-4" data-testid={testid}>
      <p className="font-display text-sm text-nk-gold mb-1.5">{label}</p>
      <p className="text-xs text-white/70 leading-relaxed">{desc}</p>
      {range && <p className="mt-2 font-mono text-[10px] text-white/45 uppercase tracking-wider">{range}</p>}
    </div>
  );
}

function ModeCard({ Icon, name, cap, formulaLines, link, gold = false }) {
  return (
    <div className={`nk-card p-5 ${gold ? "border-nk-gold/30" : ""}`} data-testid={`scoring-mode-${name.toLowerCase().replace(/\s/g,"-")}`}>
      <div className="flex items-start gap-3">
        <div className={`w-10 h-10 rounded-md flex items-center justify-center shrink-0 ${gold ? "bg-nk-gold/15 text-nk-gold" : "bg-nk-green/25 text-nk-gold"}`}>
          <Icon size={16} weight={gold ? "fill" : "duotone"} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2 mb-1 flex-wrap">
            <h3 className="font-display text-lg font-medium text-white">{name}</h3>
            {link && (
              <Link to={link} className="text-[10px] uppercase tracking-widest text-nk-gold hover:text-nk-gold/80">
                Play →
              </Link>
            )}
          </div>
          <p className="text-[11px] text-white/55 mb-2 font-mono">{cap}</p>
          <ul className="text-sm text-white/75 space-y-1">
            {formulaLines.map((l, i) => (
              <li key={i} className="flex gap-2">
                <span className="text-nk-gold/60 mt-0.5">•</span>
                <span>{l}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}

function BonusRow({ label, value }) {
  return (
    <div className="flex items-center justify-between border-b border-white/6 last:border-b-0 py-1.5">
      <span className="text-white/75">{label}</span>
      <span className="font-mono text-nk-gold text-xs">{value}</span>
    </div>
  );
}
